/**
 * The IssueRenderer: a reader-accurate render of a persisted {@link Issue}. It is
 * the right pane of the Design studio, showing the newsletter's first issue exactly
 * as a subscriber would read it: the subject as the email subject line, then the
 * KEPT blocks in running order, each block's prose rendered through a TINY ALLOWLIST
 * markdown renderer.
 *
 * SECURITY: the block `body` is model/assembly-authored text and is therefore
 * untrusted. It is NEVER passed to `dangerouslySetInnerHTML`. Instead {@link
 * renderProse} parses only paragraphs, unordered lists, and inline `[text](url)`
 * links, emitting React elements whose text children React escapes for us; any HTML
 * in the source is rendered as literal, escaped text. Link URLs are accepted only
 * when they parse as http(s) (see {@link isHttpUrl}), so a `javascript:`/`data:`
 * URL can never become an href: the whole `[text](url)` is left as escaped text.
 */
import type { ReactNode } from 'react';
import { type BadgeTone } from '@nuff/ui/react';
import type { Newsletter } from '@nuff/types';
import type { ProseInline } from '@nuff/render';
import { isHttpUrl, keptBlocksInOrder, parseProse } from '@nuff/render';
import type { ContentBlock, ContentBlockRole, Issue } from '../../api';
import { formatIssueDate } from './panels';

// Re-export the shared allowlist predicate so existing callers/tests keep a single
// `./IssueRenderer` import surface even though the logic now lives in @nuff/render.
export { isHttpUrl };

/** Human labels for each block role, shown on the left-rail curation chip. No jargon. */
export const ROLE_LABELS: Record<ContentBlockRole, string> = {
  intro: 'Intro',
  story: 'Take',
  commentary: 'Commentary',
  digest: 'Digest',
  links: 'Links',
  signoff: 'Sign-off',
};

/** The Badge tone each role reads in, so roles are visually distinct but calm. */
export const ROLE_TONES: Record<ContentBlockRole, BadgeTone> = {
  intro: 'info',
  story: 'neutral',
  commentary: 'neutral',
  digest: 'info',
  links: 'success',
  signoff: 'neutral',
};

/**
 * Whether a body already carries an inline http(s) link. The DETERMINISTIC roundup
 * (role `links`) writes its whole bullet list of `[Title](url)` links into the body, so
 * a separate {@link sourceRefs} list below would duplicate it; a legacy `links` block
 * (an AI lead-in with no inline links) still shows its sources list. So the renderer
 * shows the sources list only when the body has NOT already woven the links in.
 */
function bodyHasInlineLink(body: string): boolean {
  return /\]\(https?:\/\//.test(body);
}

/**
 * Render the parsed inline nodes of one line to React children. A link node (the
 * url already gated to http(s) by the SHARED @nuff/render allowlist) becomes an
 * anchor; a text node is emitted as a plain string that React escapes for us, so
 * any raw HTML or rejected `javascript:`/`data:` markdown reaches the DOM only as
 * inert text.
 */
function renderInline(nodes: readonly ProseInline[], keyPrefix: string): ReactNode[] {
  return nodes.map((node, index) =>
    node.type === 'link' ? (
      <a
        key={`${keyPrefix}-a${String(index)}`}
        className="nlv__issue-link"
        href={node.url}
        target="_blank"
        rel="noreferrer noopener"
      >
        {node.text}
      </a>
    ) : (
      node.value
    ),
  );
}

/**
 * Render a block's prose body to a SAFE list of React elements by walking the AST
 * produced by @nuff/render's shared allowlist parser (paragraphs, unordered lists,
 * and http(s)-only inline links). REACT rendering lives here; the parse + allowlist
 * is the exact same code @nuff/email walks to HTML/text, so the on-screen preview
 * and the sent email can never disagree about what is a link and what is inert text.
 */
export function renderProse(body: string): ReactNode[] {
  return parseProse(body).map((block, index) => {
    if (block.type === 'list') {
      const k = `ul${String(index)}`;
      return (
        <ul key={k} className="nlv__issue-ul">
          {block.items.map((item, i) => (
            <li key={i}>{renderInline(item, `${k}-${String(i)}`)}</li>
          ))}
        </ul>
      );
    }
    const k = `p${String(index)}`;
    return (
      <p key={k} className="nlv__issue-p">
        {renderInline(block.children, k)}
      </p>
    );
  });
}

/**
 * Normalise a set of section ids into an order- and duplicate-independent key:
 * sorted, de-duplicated, and space-joined (section ids never contain spaces). Both
 * sides of the staleness comparison run through this so ONLY a genuine change in
 * WHICH sections are present can differ, never their order.
 */
export function sectionSetKey(ids: Iterable<string>): string {
  return [...new Set(ids)].sort().join(' ');
}

/**
 * A signature of the SET of sections an issue was assembled from: the `sectionId`s
 * carried by ALL of its section-derived blocks, KEPT AND DISMISSED (intro/signoff
 * carry none), normalised through {@link sectionSetKey}. Compared against the
 * normalised set of currently-SELECTED section ids, it tells the studio whether a
 * loaded draft still matches the current curation, so a draft assembled from a
 * genuinely different selection can auto-regenerate while an up-to-date one never
 * re-bills.
 *
 * Deliberately SET-based over every section block (not just kept, and not in
 * reading order): dismissing or reordering a BLOCK is a manual curation edit, not a
 * section add/remove, so it must NOT change this signature and trigger a billed
 * regenerate that would discard the owner's edits. Only adding or removing a
 * selected section moves the signature.
 */
export function issueSectionSignature(issue: Issue): string {
  return sectionSetKey(
    issue.blocks.map((block) => block.sectionId).filter((id): id is string => id !== undefined),
  );
}

/**
 * One rendered block: its optional title, prose, and (for links) sources. A REAL
 * newsletter never labels its opening "INTRO" or its closer "SIGN-OFF", so the role
 * chip is deliberately NOT rendered here; it lives only on the left-rail BlockCard
 * (the curation view). The stable `id` lets the rail wire selection/scroll to a
 * block without a retrofit later.
 */
function IssueBlock({
  block,
  active = false,
  busy = false,
  updated = false,
  onHover,
  onSelect,
}: {
  readonly block: ContentBlock;
  /** Highlighted because its rail card is hovered/focused (the two-pane link). */
  readonly active?: boolean;
  /** This block is being regenerated on its own: dim it and mark it busy. */
  readonly busy?: boolean;
  /** This block was JUST regenerated: a brief "updated" wash confirms the swap. */
  readonly updated?: boolean;
  /** Report hover/leave so the matching rail card can highlight in step. */
  readonly onHover?: ((id: string | null) => void) | undefined;
  /** Clicking the rendered block jumps focus to its rail card for editing. */
  readonly onSelect?: ((id: string) => void) | undefined;
}): React.JSX.Element {
  return (
    <section
      className={`nlv__issue-block${active ? ' nlv__issue-block--active' : ''}${
        busy ? ' nlv__issue-block--busy' : ''
      }${updated ? ' nlv__issue-block--updated' : ''}${
        onSelect !== undefined ? ' nlv__issue-block--linked' : ''
      }`}
      data-role={block.role}
      aria-busy={busy ? true : undefined}
      id={`issue-block-${block.id}`}
      {...(onHover !== undefined
        ? {
            onMouseEnter: () => onHover(block.id),
            onMouseLeave: () => onHover(null),
          }
        : {})}
      {...(onSelect !== undefined
        ? {
            onClick: (event: React.MouseEvent<HTMLElement>) => {
              // Never hijack a click on a real link inside the prose.
              if ((event.target as HTMLElement).closest('a') !== null) return;
              onSelect(block.id);
            },
          }
        : {})}
    >
      {block.title !== undefined && block.title.length > 0 && (
        <h3 className="nlv__issue-block-title">{block.title}</h3>
      )}

      {block.body.length > 0 && <div className="nlv__issue-prose">{renderProse(block.body)}</div>}

      {block.role === 'links' &&
        !bodyHasInlineLink(block.body) &&
        block.sourceRefs !== undefined &&
        block.sourceRefs.length > 0 && (
        <ul className="nlv__issue-links">
          {block.sourceRefs.map((ref, index) => (
            <li key={index}>
              {isHttpUrl(ref.url) ? (
                <a
                  className="nlv__issue-link"
                  href={ref.url}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  {ref.title}
                </a>
              ) : (
                <span>{ref.title}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * The reader-accurate render of an issue: subject line then the kept blocks in
 * order. This REPLACES the old PreviewHero as the studio's right pane.
 */
export function IssueRenderer({
  issue,
  newsletter,
  highlightId = null,
  busyBlockId = null,
  updatedBlockId = null,
  onBlockHover,
  onBlockSelect,
}: {
  readonly issue: Issue;
  /** The newsletter, so the reader pane can print a real from-line + issue date. */
  readonly newsletter?: Newsletter;
  /** The id of the block whose rail card is hovered/focused, highlighted in step. */
  readonly highlightId?: string | null;
  /** The id of the block being regenerated on its own: it dims + reads busy. */
  readonly busyBlockId?: string | null;
  /** The id of the block just regenerated: a brief "updated" confirmation wash. */
  readonly updatedBlockId?: string | null;
  readonly onBlockHover?: (id: string | null) => void;
  readonly onBlockSelect?: (id: string) => void;
}): React.JSX.Element {
  const blocks = keptBlocksInOrder(issue);
  // The from-line: the author/brand byline (falling back to the newsletter name),
  // and the scheduled issue date when one is set, so the pane reads as a real email.
  const byline =
    newsletter?.authorName !== undefined && newsletter.authorName.length > 0
      ? newsletter.authorName
      : newsletter?.name;
  const issueDate =
    newsletter?.nextIssueAt !== undefined && newsletter.nextIssueAt.length > 0
      ? formatIssueDate(newsletter.nextIssueAt)
      : null;
  const metaParts: string[] = [];
  if (byline !== undefined && byline.length > 0) metaParts.push(`From ${byline}`);
  if (issueDate !== null) metaParts.push(issueDate);
  // No aria-label here: the studio wraps this in a <section aria-labelledby> whose
  // heading is already "Your first/next issue", so naming the article too would
  // just duplicate that accessible name.
  return (
    <article className="nlv__issue">
      <header className="nlv__issue-head">
        <p className="nlv__issue-subject">
          <span className="nlv__issue-subject-label">Subject</span>
          {issue.subject}
        </p>
        {metaParts.length > 0 && <p className="nlv__issue-from">{metaParts.join(' · ')}</p>}
      </header>
      {blocks.map((block) => (
        <IssueBlock
          key={block.id}
          block={block}
          active={highlightId === block.id}
          busy={busyBlockId === block.id}
          updated={updatedBlockId === block.id}
          onHover={onBlockHover}
          onSelect={onBlockSelect}
        />
      ))}
    </article>
  );
}
